"use client";

import React from "react";
import { Download, Settings, Box, Zap, Info, Terminal, Cpu, Layers } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
const tabTriggers = [{
  value: "auto",
  label: "Proactive Support",
  icon: Zap
}, {
  value: "manual",
  label: "Infrastructure Setup",
  icon: Settings
}];
const autoSteps = [{
  id: 1,
  title: "Local Response",
  desc: "Our Rapid City team responds to your tickets in minutes, not days.",
  icon: Download
}, {
  id: 2,
  title: "Simple Explanations",
  desc: "We explain technical solutions in plain English so you stay in the loop.",
  icon: Box
}, {
  id: 3,
  title: "Proactive Monitoring",
  desc: "We fix potential issues before they cause downtime for your business.",
  icon: Terminal
}];
const manualSteps = [{
  id: 1,
  title: "Site Assessment",
  desc: "We map out your physical space for optimal wireless and cable routing.",
  icon: Layers
}, {
  id: 2,
  title: "Clean Installation",
  desc: "Professional rack organization and cable pulling for a tidy server room.",
  icon: Cpu
}, {
  id: 3,
  title: "Future Proofing",
  desc: "We build infrastructure that scales with your business growth in South Dakota.",
  icon: Settings
}];
const autoHotspots = [{
  id: 1,
  top: "25%",
  left: "35%",
  title: "99.9% Uptime",
  desc: "Our proactive monitoring ensures your systems stay online around the clock."
}, {
  id: 2,
  top: "65%",
  left: "75%",
  title: "Security First",
  desc: "Enterprise-grade cybersecurity protocols applied to every managed device."
}, {
  id: 3,
  top: "45%",
  left: "15%",
  title: "Cloud Backup",
  desc: "Automated off-site backups to protect your vital business data."
}];
const manualHotspots = [{
  id: 1,
  top: "30%",
  left: "50%",
  title: "Cat6 Cabling",
  desc: "High-speed physical connections for maximum network reliability."
}, {
  id: 2,
  top: "70%",
  left: "30%",
  title: "WAP Mapping",
  desc: "Strategic placement of wireless access points for 100% coverage."
}, {
  id: 3,
  top: "50%",
  left: "80%",
  title: "Rack Cleanup",
  desc: "Transforming 'spaghetti' wiring into organized, labeled systems."
}];
export default function StudioCompatibility() {
  return <section data-section-id="224232" id="why-rapid-tech-works" className="dark bg-background text-foreground py-24 relative overflow-hidden border-t border-border">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(20,85,147,0.05)_0%,transparent_70%)] pointer-events-none"></div>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-64 bg-primary/10 blur-[120px] rounded-full pointer-events-none"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-mono mb-6">
            <Terminal className="size-4" />
            <span>WHY_RAPID_TECH</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold font-default mb-6 tracking-tight text-foreground">
            Why Rapid Tech Works?
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            We combine enterprise-level IT expertise with the heart of a local South Dakota business. Discover how our proactive approach keeps your technology running smoothly.
          </p>
        </div>

        <Tabs defaultValue="auto" className="w-full max-w-6xl mx-auto">
          <div className="flex justify-center mb-12">
            <TabsList className="bg-muted/50 border border-border p-1 rounded-lg">
              {tabTriggers.map((tab, i) => {
              const Icon = tab.icon;
              return <TabsTrigger key={tab.value} value={tab.value} data-index={i} className="data-[state=active]:bg-primary/20 data-[state=active]:text-primary data-[state=active]:border-primary/50 border border-transparent px-6 py-3 text-base font-default">
                    <Icon className="size-4 mr-2" />
                    {tab.label}
                  </TabsTrigger>;
            })}
            </TabsList>
          </div>

          {}
          <TabsContent value="auto" className="space-y-8 animate-in fade-in-50 slide-in-from-bottom-4 duration-500">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-5 space-y-6">
                <div className="space-y-2">
                  <h3 className="text-2xl font-bold font-default text-foreground">99.9% System Uptime</h3>
                  <p className="text-muted-foreground">
                    Our Managed Services are designed to be invisible. We handle the updates, security, and maintenance so you can focus on running your business.
                  </p>
                </div>
                
                <div className="space-y-4 relative before:absolute before:inset-y-0 before:left-[19px] before:w-[2px] before:bg-border">
                  {autoSteps.map((step, i) => {
                  const Icon = step.icon;
                  return <div key={step.id} data-index={i} className="relative pl-12">
                        <div className="absolute left-0 top-1 size-10 rounded-full bg-background border-2 border-primary flex items-center justify-center z-10 shadow-lg">
                          <Icon className="size-5 text-primary" />
                        </div>
                        <h4 className="text-lg font-semibold font-default text-foreground mb-1">{step.title}</h4>
                        <p className="text-sm text-muted-foreground">{step.desc}</p>
                      </div>;
                })}
                </div>

                <div className="pt-4">
                  <Button variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 rounded-full px-8">
                    <Download className="mr-2 size-4" />
                    View Service Plans
                  </Button>
                </div>
              </div>

              <div className="lg:col-span-7">
                <Card className="bg-card/50 border-border overflow-hidden relative group rounded-2xl shadow-2xl">
                  <div className="absolute inset-0 bg-gradient-to-tr from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <CardHeader className="border-b border-border bg-muted/30 py-3 px-4 flex flex-row items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Box className="size-4 text-muted-foreground" />
                      <span className="text-sm font-mono text-muted-foreground">Network_Health_Monitor.sys</span>
                    </div>
                    <div className="flex gap-1.5">
                      <div className="size-2.5 rounded-full bg-primary/40"></div>
                      <div className="size-2.5 rounded-full bg-primary/40"></div>
                      <div className="size-2.5 rounded-full bg-primary/40"></div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0 relative aspect-video bg-slate-900">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1646044/img/it_monitoring_dashboard.png" alt="Rapid Tech Works Monitoring Dashboard" className="w-full h-full object-cover opacity-60" />
                    
                    <TooltipProvider delayDuration={100}>
                      {autoHotspots.map((spot, i) => <div key={spot.id} data-index={i} className="absolute" style={{
                      top: spot.top,
                      left: spot.left
                    }}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="relative flex size-6 items-center justify-center cursor-help">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75"></span>
                                <span className="relative inline-flex size-3 rounded-full bg-primary shadow-lg"></span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs border border-primary/30 bg-card/95 backdrop-blur-md shadow-2xl rounded-lg">
                              <div className="space-y-1.5">
                                <p className="font-default text-xs font-bold text-primary uppercase tracking-wider">{spot.title}</p>
                                <p className="text-xs text-muted-foreground">{spot.desc}</p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </div>)}
                    </TooltipProvider>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {}
          <TabsContent value="manual" className="space-y-8 animate-in fade-in-50 slide-in-from-bottom-4 duration-500">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-5 space-y-6">
                <div className="space-y-2">
                  <h3 className="text-2xl font-bold font-default text-foreground">Physical Infrastructure</h3>
                  <p className="text-muted-foreground">
                    From new construction to office remodels, we handle the heavy lifting of network cabling and hardware deployment with precision.
                  </p>
                </div>
                
                <div className="space-y-4 relative before:absolute before:inset-y-0 before:left-[19px] before:w-[2px] before:bg-border">
                  {manualSteps.map((step, i) => {
                  const Icon = step.icon;
                  return <div key={step.id} data-index={i} className="relative pl-12">
                        <div className="absolute left-0 top-1 size-10 rounded-full bg-background border-2 border-secondary flex items-center justify-center z-10 shadow-lg">
                          <Icon className="size-5 text-secondary" />
                        </div>
                        <h4 className="text-lg font-semibold font-default text-foreground mb-1">{step.title}</h4>
                        <p className="text-sm text-muted-foreground">{step.desc}</p>
                      </div>;
                })}
                </div>

                <div className="pt-4">
                  <Button variant="outline" className="border-secondary/50 text-secondary hover:bg-secondary/10 rounded-full px-8">
                    <Info className="mr-2 size-4" />
                    Infrastructure Services
                  </Button>
                </div>
              </div>

              <div className="lg:col-span-7">
                <Card className="bg-card/50 border-border overflow-hidden relative group rounded-2xl shadow-2xl">
                  <div className="absolute inset-0 bg-gradient-to-tr from-secondary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <CardHeader className="border-b border-border bg-muted/30 py-3 px-4 flex flex-row items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Settings className="size-4 text-muted-foreground" />
                      <span className="text-sm font-mono text-muted-foreground">Infrastructure_Blueprint.map</span>
                    </div>
                    <div className="flex gap-1.5">
                      <div className="size-2.5 rounded-full bg-secondary/40"></div>
                      <div className="size-2.5 rounded-full bg-secondary/40"></div>
                      <div className="size-2.5 rounded-full bg-secondary/40"></div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0 relative aspect-video bg-slate-900">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1646044/img/network_cabling_installation.png" alt="Professional Network Rack Installation" className="w-full h-full object-cover opacity-60" />
                    
                    <TooltipProvider delayDuration={100}>
                      {manualHotspots.map((spot, i) => <div key={spot.id} data-index={i} className="absolute" style={{
                      top: spot.top,
                      left: spot.left
                    }}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="relative flex size-6 items-center justify-center cursor-help">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-secondary opacity-75"></span>
                                <span className="relative inline-flex size-3 rounded-full bg-secondary shadow-lg"></span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs border border-secondary/30 bg-card/95 backdrop-blur-md shadow-2xl rounded-lg">
                              <div className="space-y-1.5">
                                <p className="font-default text-xs font-bold text-secondary uppercase tracking-wider">{spot.title}</p>
                                <p className="text-xs text-muted-foreground">{spot.desc}</p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </div>)}
                    </TooltipProvider>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>;
}